<template>
  <UiModalShell
    :show="props.showModal"
    title="Add Customer"
    size="lg"
    @close="closeModal"
  >
    <!-- Manual add sub-modal (rendered alongside — not inside — via its own teleport) -->
    <ManualAddCustomerForm
      :showModal="showManualAdd"
      :preselectedSiteId="selectedSiteFilter"
      @close="showManualAdd = false"
      @added="handleManualAdded"
    />

    <!-- Body -->
    <form id="customer-modal-form" @submit.prevent="submitForm" class="space-y-5">
      <!-- Error -->
      <div
        v-if="error"
        class="p-3 bg-[rgba(240,82,82,0.08)] border border-[rgba(240,82,82,0.35)] rounded-[var(--radius-md)]"
      >
        <p class="text-err text-[13px]">{{ error }}</p>
      </div>

      <!-- Account fields -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="block font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3 mb-1.5">
            First name <span class="text-err">*</span>
          </label>
          <UiInput
            v-model="first_name"
            placeholder="First name"
          />
        </div>
        <div>
          <label class="block font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3 mb-1.5">
            Last name <span class="text-err">*</span>
          </label>
          <UiInput
            v-model="last_name"
            placeholder="Last name"
          />
        </div>
        <div>
          <label class="block font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3 mb-1.5">
            Email <span class="text-err">*</span>
          </label>
          <UiInput
            v-model="email"
            type="email"
            placeholder="name@company.com"
          />
        </div>
        <div>
          <label class="block font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3 mb-1.5">
            Contact number <span class="text-err">*</span>
          </label>
          <UiInput
            v-model="contact_number"
            type="tel"
            placeholder="+971 …"
          />
        </div>
      </div>

      <!-- Picker section -->
      <div class="space-y-3">
        <div class="flex items-center justify-between gap-3">
          <label class="block font-mono text-[10.5px] uppercase tracking-[0.14em] text-text-3">
            Primary customer <span class="text-err">*</span>
          </label>
          <button
            type="button"
            @click="showManualAdd = true"
            class="font-mono text-[11px] text-lime hover:underline"
          >
            + Manually add customer
          </button>
        </div>

        <!-- Filters -->
        <div class="grid grid-cols-1 sm:grid-cols-12 gap-3">
          <div class="sm:col-span-7">
            <UiInput
              v-model="search"
              :left-icon="Search"
              clearable
              placeholder="Search by customer name or number…"
            />
          </div>
          <div class="sm:col-span-5">
            <UiSelect
              v-model="selectedSiteFilter"
              :disabled="sitesLoading"
              @update:modelValue="onSiteFilterChanged"
            >
              <option :value="null">All sites</option>
              <option v-for="site in sites" :key="site.id" :value="site.id">
                {{ site.display_name }}
              </option>
            </UiSelect>
          </div>
        </div>

        <!-- Picker table -->
        <div class="border border-line rounded-[var(--radius-md)] overflow-hidden">
          <div class="max-h-[280px] overflow-y-auto">
            <UiTable
              :columns="[
                { key: 'site', label: 'Site' },
                { key: 'cust_no', label: 'Cust No' },
                { key: 'name', label: 'Name' },
                { key: 'state', label: '' },
              ]"
            >
              <tr
                v-for="row in filteredCustomers"
                :key="row.id"
                class="cursor-pointer"
                :class="{ 'bg-[rgba(184,230,0,0.05)]': isSelected(row) }"
                @click="selectedCustomer = row"
              >
                <td>
                  <UiBadge tone="lime">{{ row.site_name }}</UiBadge>
                </td>
                <td class="mono">{{ row.customer_no }}</td>
                <td>{{ row.customer_name }}</td>
                <td class="text-right">
                  <UiStatusPill v-if="isSelected(row)" status="ok">selected</UiStatusPill>
                </td>
              </tr>
            </UiTable>

            <div v-if="customersLoading" class="text-center py-8">
              <Loader2 :size="20" class="inline-block animate-spin text-lime" />
              <p class="mt-2 text-text-3 text-[12.5px]">Loading customers…</p>
            </div>
            <div
              v-else-if="filteredCustomers.length === 0"
              class="text-center py-8 text-text-3 text-[12.5px]"
            >
              No customers match the current filters.
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between font-mono text-[11px] text-text-3">
          <span v-if="selectedCustomer">
            Selected: {{ selectedCustomer.customer_no }} — {{ selectedCustomer.customer_name }}
          </span>
          <span v-else>No customer selected.</span>
          <span v-if="mcsCustomersTotal > mcsCustomers.length">
            Showing first {{ mcsCustomers.length }} of {{ mcsCustomersTotal }}
          </span>
        </div>
      </div>
    </form>

    <template #footer>
      <UiButton
        variant="ghost"
        :disabled="saving"
        @click="closeModal"
      >
        Cancel
      </UiButton>
      <UiButton
        variant="primary"
        type="submit"
        form="customer-modal-form"
        :loading="saving"
        :disabled="saving || !selectedCustomer"
      >
        Save Customer
      </UiButton>
    </template>
  </UiModalShell>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { AdminApiService } from "@/services/adminApi";
import ManualAddCustomerForm from "@/components/ManualAddCustomerForm.vue";
import UiModalShell from "@/components/ui/UiModalShell.vue";
import UiButton from "@/components/ui/UiButton.vue";
import UiInput from "@/components/ui/UiInput.vue";
import UiSelect from "@/components/ui/UiSelect.vue";
import UiTable from "@/components/ui/UiTable.vue";
import UiBadge from "@/components/ui/UiBadge.vue";
import UiStatusPill from "@/components/ui/UiStatusPill.vue";
import { Search, Loader2 } from "lucide-vue-next";

const props = defineProps({
  showModal: Boolean,
  // Kept for backwards compat — no longer consumed; MCS customers are fetched live.
  customersList: {
    type: Array,
    default: () => [],
  },
});

// The modal now performs the full create + MCS-assignment flow in-component
// (see submitForm) and emits `created` with the result. The legacy `save`
// emit is intentionally dropped: Dashboard.vue's existing `@save` listener
// would otherwise fire a duplicate `createAdminAccount` call on top of the
// one this modal now makes itself. See Plan 7 Task 7 report for rationale.
const emit = defineEmits(["close", "created"]);

const store = useStore();
const adminApi = new AdminApiService(store.state.apiUrl);

// Form state
const first_name = ref("");
const last_name = ref("");
const email = ref("");
const contact_number = ref("");
const selectedCustomer = ref(null); // full McsCustomerListItem row
const selectedSiteFilter = ref(null);

// Data
const sites = ref([]);
const mcsCustomers = ref([]);
const mcsCustomersTotal = ref(0);

// UI state
const saving = ref(false);
const sitesLoading = ref(false);
const customersLoading = ref(false);
const error = ref(null);
const showManualAdd = ref(false);
const search = ref("");

// Derived: client-side filter across already-loaded MCS customers.
const filteredCustomers = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return mcsCustomers.value;
  return mcsCustomers.value.filter((c) => {
    const no = String(c.customer_no ?? "").toLowerCase();
    const name = String(c.customer_name ?? "").toLowerCase();
    const siteName = String(c.site_name ?? "").toLowerCase();
    return no.includes(q) || name.includes(q) || siteName.includes(q);
  });
});

function isSelected(row) {
  return selectedCustomer.value && selectedCustomer.value.id === row.id;
}

async function fetchSites() {
  sitesLoading.value = true;
  try {
    const data = await adminApi.listSites();
    sites.value = Array.isArray(data) ? data.filter((s) => s.is_active) : [];
  } catch (err) {
    console.error("Failed to load sites:", err);
    sites.value = [];
  } finally {
    sitesLoading.value = false;
  }
}

async function fetchCustomers() {
  customersLoading.value = true;
  try {
    const res = await adminApi.listMcsCustomers({
      site_id: selectedSiteFilter.value,
      limit: 500,
      offset: 0,
    });
    mcsCustomers.value = Array.isArray(res?.data) ? res.data : [];
    mcsCustomersTotal.value = res?.meta?.total ?? mcsCustomers.value.length;
  } catch (err) {
    console.error("Failed to load MCS customers:", err);
    mcsCustomers.value = [];
    mcsCustomersTotal.value = 0;
  } finally {
    customersLoading.value = false;
  }
}

function onSiteFilterChanged() {
  // Re-fetch customers for the chosen site and clear any selection that no
  // longer matches the filter.
  if (
    selectedCustomer.value
    && selectedSiteFilter.value != null
    && selectedCustomer.value.site_id !== selectedSiteFilter.value
  ) {
    selectedCustomer.value = null;
  }
  fetchCustomers();
}

// Reset the form + refetch whenever the modal is (re-)opened
watch(
  () => props.showModal,
  async (isOpen) => {
    if (!isOpen) return;
    error.value = null;
    first_name.value = "";
    last_name.value = "";
    email.value = "";
    contact_number.value = "";
    selectedCustomer.value = null;
    selectedSiteFilter.value = null;
    search.value = "";
    if (!sites.value.length) await fetchSites();
    await fetchCustomers();
  },
  { immediate: false }
);

onMounted(async () => {
  if (props.showModal) {
    await fetchSites();
    await fetchCustomers();
  }
});

const closeModal = () => {
  if (saving.value) return;
  emit("close");
};

function handleManualAdded(created) {
  showManualAdd.value = false;
  if (!created) return;
  // Push into the local list if not already present and select it
  if (!mcsCustomers.value.some((c) => c.id === created.id)) {
    mcsCustomers.value.unshift(created);
    mcsCustomersTotal.value += 1;
  }
  selectedCustomer.value = created;
}

async function submitForm() {
  error.value = null;
  if (!selectedCustomer.value) {
    error.value = "Please select a primary customer.";
    return;
  }
  if (!first_name.value || !last_name.value || !email.value || !contact_number.value) {
    error.value = "Please fill in all required fields.";
    return;
  }
  saving.value = true;

  // Legacy create payload — matches AdminAccount Pydantic model at
  // app/api/admin_users.py. We intentionally send customers=[] because the
  // backend create endpoint is not MCS-aware; the MCS assignment is performed
  // in a second step via updateUserMcsCustomers, which writes
  // Users/{uid}.customers_mcs[] and flips the catalog row to activated.
  const accountPayload = {
    first_name: first_name.value,
    last_name: last_name.value,
    email: email.value,
    customer_logo: "URL",
    contact_number: contact_number.value,
    primary_account: String(selectedCustomer.value.id ?? ""),
    role: "ACCOUNT_ADMIN",
    account_type: "CUSTOMER",
    customers: [],
  };

  try {
    // Step 1 — create the Firebase Auth user + Firestore doc
    const createRes = await adminApi.createAdminAccount(accountPayload);
    const uid = createRes?.uid;
    if (!uid) {
      throw new Error("Create account response missing uid.");
    }

    // Step 2 — link the MCS customer so:
    //   - postgres mcs_customer_user_links row is inserted
    //   - is_activated flips to true + backfill_status=pending
    //   - Firestore Users/{uid}.customers_mcs[] is populated
    //   - backfill is enqueued
    try {
      await adminApi.updateUserMcsCustomers(uid, [selectedCustomer.value.id]);
    } catch (mcsErr) {
      // The user IS created; surface a soft warning. Admin can retry from the
      // assignment modal.
      console.warn("MCS assignment after create failed:", mcsErr);
      error.value =
        `User created but MCS assignment failed: ${mcsErr.message || mcsErr}. `
        + "Open the user's assignments to retry.";
      emit("created", {
        uid,
        account: accountPayload,
        mcs_customer: selectedCustomer.value,
        mcs_assignment_failed: true,
        error: mcsErr.message || String(mcsErr),
      });
      return;
    }

    // Success — notify parent
    emit("created", {
      uid,
      account: accountPayload,
      mcs_customer: selectedCustomer.value,
      mcs_assignment_failed: false,
    });

    // Reset the form
    first_name.value = "";
    last_name.value = "";
    email.value = "";
    contact_number.value = "";
    selectedCustomer.value = null;
    closeModal();
  } catch (err) {
    console.error("Create customer account failed:", err);
    error.value = err.message || "Failed to create account.";
  } finally {
    saving.value = false;
  }
}
</script>
